import { useQuery } from "react-query";
import { TableColumnsType } from "antd";
import {
  changeSortOrderAntd,
  CommonTable,
  onSortAntd,
} from "components/atoms/table/AntdTable";
import {
  SUBSCRIPTION_TYPE,
  SubscriptionPageOutletContext,
  SubscriptionRequestList,
  SubscriptionRequestListResponse,
  SubscriptionRequestTable,
} from "api/interfaces/subscription.interface";
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { getSubscriptonRequestList } from "api/subscriptionAPI";
import { ProfileAccountInfo } from "api/interfaces/accountInterface.interface";
import { useAppSelector } from "redux/hooks";
import { formatNumber, formatUppercaseOnly } from "utils/functions";
import { getAccountTimeFormatDate } from "utils/timeUtil";
import {
  BasePagination,
  SORT_DIRECTION,
} from "api/interfaces/commonInterface.interface";
import TableColumnFilterTitle from "components/blocks/table/TableColumnFilterTitle";
import { useKeyType } from "hook/subscription/useKeyType";
import { baseQueryInfo, useQueryKey } from "utils/queryUtil";
import * as mediaQuery from "components/MediaQuery";
import { StatusType } from "components/blocks/status/Status";
import { selectSubscriptionStomp } from "redux/stomp/stompSelector";
import { useOutletContext, useSearchParams } from "react-router-dom";

type Props = {
  titleComponent?: React.ReactNode | string;
  status?: StatusType;
  button?: React.ReactNode;
  isCoTermOpen?: boolean;
};
export default function EuRequestHistory(props: Props): JSX.Element {
  const selectedAccount: ProfileAccountInfo = useAppSelector(
    (state) => state.accountSettings
  );
  const { subscriptionRequestUpdated, subscriptionConfirmUpdated } =
    useAppSelector(selectSubscriptionStomp);
  const isMobile = mediaQuery.useIsMobile();
  const isBigScreen = mediaQuery.useIsBigScreen();
  const accountId = useMemo(() => {
    return selectedAccount.accountId;
  }, [selectedAccount.accountId]);

  const { keyTypeOptions, durationMonthOptions } = useKeyType();
  const { onHeaderCell, onChangePage, onClickFilterTitle, setStatus } =
    useOutletContext<SubscriptionPageOutletContext>();
  const [pageInfo, setPageInfo] = useState<Partial<BasePagination>>({
    ...baseQueryInfo,
    sortType: "createdAt",
  });
  const [searchParams] = useSearchParams();
  const queryInfo = useMemo<BasePagination>(() => {
    const get = (key: string) => searchParams.get(key);

    return {
      ...baseQueryInfo,
      ...pageInfo,
      ...(get("pageNum") && { pageNum: Number(get("pageNum")) }),
      ...(get("pageLimit") && { pageLimit: Number(get("pageLimit")) }),
      ...{ keyword: get("keyword") ?? "" },
      ...(get("sortType") && { sortType: get("sortType")! }),
      ...(get("sortDirection") && {
        sortDirection: get("sortDirection") as SORT_DIRECTION,
      }),
      ...(get("type") && { type: get("type") }),
    };
  }, [searchParams, pageInfo]);
  const queryKey = useQueryKey(queryInfo);

  const { data, isFetching, refetch } = useQuery(
    ["getSubscriptonRequestList", accountId, queryKey],
    () =>
      getSubscriptonRequestList({
        accountId,
        payload: queryInfo,
      }),
    {
      retry: 0,
      refetchOnWindowFocus: false,
      onSuccess: (res: SubscriptionRequestListResponse) => {
        if (res.page) {
          setStatus({
            totalTitle: "Total",
            totalValue: res.page.total,
          });
          setPageInfo((query) => {
            return {
              ...query,
              ...res.page,
            };
          });
        }
      },
    }
  );

  useEffect(() => {
    if (
      (subscriptionRequestUpdated.updatedDate &&
        subscriptionRequestUpdated.accountId === accountId) ||
      (subscriptionConfirmUpdated.updatedDate &&
        subscriptionConfirmUpdated.accountId === accountId)
    ) {
      refetch();
    }
  }, [subscriptionRequestUpdated, subscriptionConfirmUpdated]);

  const columns: TableColumnsType<SubscriptionRequestTable> = [
    {
      title: (
        <TableColumnFilterTitle
          title="Type"
          filters={keyTypeOptions}
          onClick={(value: string | undefined) => {
            onClickFilterTitle(value, "type");
          }}
          data-testid="subscription-modal-eu-request-table-column-filter-title-click-filter-title"
        />
      ),

      dataIndex: "keyType",
      render: (value, row) => formatUppercaseOnly(value),
      width: 90,
    },
    {
      title: "Requester",
      dataIndex: "requester",
      key: "requester",
    },
    {
      title: "Installer",
      dataIndex: "installer",
      key: "installer",
      width: isBigScreen ? "30%" : "20%",
      responsive: props.isCoTermOpen ? ["xxl"] : ["xl"],
    },
    {
      title: "QTY",
      dataIndex: "qty",
      key: "qty",
      render: (value, row) => {
        let unit = "CH";
        if (row.keyType === SUBSCRIPTION_TYPE.STORAGE) {
          unit = "TB";
        }
        return `${formatNumber(value)} ${unit}`;
      },
      width: 100,
    },
    {
      title: "Duration",
      dataIndex: "duration",
      sorter: true,
      sortOrder: changeSortOrderAntd(queryInfo, "duration"),
      key: "duration",
      defaultSortOrder: "descend",
      onHeaderCell: onHeaderCell,
      render: (value, row) =>
        durationMonthOptions(row.keyType as SUBSCRIPTION_TYPE, true).find(
          (opt) => opt.value === String(value)
        )?.label || "-",
      width: isMobile ? 100 : 180,
    },
    {
      title: "Created At",
      dataIndex: "createdAt",
      sorter: true,
      key: "createdAt",
      sortOrder: changeSortOrderAntd(queryInfo, "createdAt"),
      defaultSortOrder: "descend",
      onHeaderCell: onHeaderCell,
      render: (value, row) => {
        return getAccountTimeFormatDate(value, selectedAccount);
      },
      responsive: ["lg"],
      width: 100,
    },
    {
      title: "Request Status",
      dataIndex: "isConfirm",
      render: (value) => (value ? "Completed" : "Applied"),
      width: 130,
    },
    {
      title: "Message",
      dataIndex: "message",
      key: "message",
      responsive: ["xxl"],
      width: 200,
    },
  ];

  const requestList: SubscriptionRequestTable[] = useMemo(() => {
    if (data && data.result && data.error === 0) {
      return data.result.map((res: SubscriptionRequestList) => {
        return {
          ...res,
          qty:
            res.keyType === SUBSCRIPTION_TYPE.STORAGE
              ? res.capacity
              : res.channels,
        };
      });
    }
    return [];
  }, [data]);

  return (
    <CommonTable
      columns={columns}
      dataSource={requestList}
      loading={isFetching}
      queryInfo={queryInfo}
      onChangePage={onChangePage}
    />
  );
}
